// Bring-your-own-song upload card — the at-the-computer surface. When the learner loads
// their own `.mid`, this shows the deterministic analysis (key · chords · progression ·
// accompaniment texture · estimated difficulty, with any out-of-vocabulary chords flagged)
// and a "Show my path" action that opens the path view on the map. A thin DOM blit over a
// pure `UploadPlan` (songs/upload-plan); no curriculum logic here. See
// docs/architecture.md → "Bring-your-own-song (upload)".
import { chordLabel } from "../songs/chord-atoms";
import { skillLabel } from "../songs/skills";
import type { UploadPlan } from "../songs/upload-plan";
import { el } from "./dom";

const list = (atoms: readonly string[], label: (a: string) => string): string =>
  atoms.length > 0 ? atoms.map(label).join(", ") : "—";

/** Show the analysis card for an uploaded song. Returns nothing; self-removes on close or
 *  when "Show my path" is clicked (which also calls `onShowPath`). */
export function showUploadCard(plan: UploadPlan, onShowPath: () => void): void {
  const overlay = el("div", { class: "upload-overlay" });
  const card = el("div", { class: "upload-card" });
  const row = (k: string, v: string): HTMLElement => {
    const r = el("div", { class: "upload-row" });
    r.append(el("span", { class: "upload-key", textContent: k }), el("span", { textContent: v }));
    return r;
  };

  const close = (): void => overlay.remove();
  overlay.onclick = (ev) => {
    if (ev.target === overlay) close();
  };

  card.append(
    el("h3", { textContent: "Your song" }),
    row("Key", plan.key),
    row("Chords", list(plan.chords, chordLabel)),
    row("Progression", list(plan.progressions, skillLabel)),
    row("Accompaniment", list(plan.texture, skillLabel)),
    row("Est. difficulty", `~grade ${Math.round(plan.difficulty)}`),
  );
  if (plan.beyond.length > 0) {
    card.append(
      el("div", {
        class: "upload-beyond",
        textContent: `Beyond what we teach yet: ${list(plan.beyond, chordLabel)}`,
      }),
    );
  }

  const showPath = el<HTMLButtonElement>("button", {
    class: "primary upload-show-path",
    textContent: "🧭 Show my path",
  });
  showPath.onclick = () => {
    close();
    onShowPath();
  };
  const dismiss = el<HTMLButtonElement>("button", {
    class: "upload-close",
    textContent: "✕ Close",
  });
  dismiss.onclick = close;
  const actions = el("div", { class: "upload-actions" });
  actions.append(showPath, dismiss);
  card.append(actions);

  overlay.append(card);
  document.body.append(overlay);
}
